import { useMemo, useRef } from "react";
import { Redirect, useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconChevronRight, IconHash } from "@tabler/icons-react";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { NavLang } from "../../components/app/headers/layout";
import ArticlePublicationCard from "../../components/cards/ArticlePublicationCard";
import Loader from "../../components/Loader";
import SkeletonPublicationCard from "../../components/skeleton/SkeletonPublicationCard";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { Button } from "../../components/ui/Button";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { SortSelect } from "../../components/ui/SortSelect";
import { TabButton } from "../../components/ui/TabButton";
import { isDesktopLayout } from "../../common/hooks/useIsDesktopView";
import { useJournalFollow } from "../../domains/journal/hooks/useJournalFollow";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { useActiveLang } from "../../domains/app/hooks/useActiveLang";
import { usePageScroll } from "../../domains/app/hooks/usePageScroll";
import { useEffectOnce } from "../../domains/app/hooks/useEffectOnce";
import { useApiInfiniteQuery, useApiQuery } from "../../services/api";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";

export function formatNumber(number: string | number) {
  const num = typeof number === "string" ? Number.parseFloat(number) : number;

  if (!num || Number.isNaN(num)) return number; // Return original if not a valid number

  if (num > 10) {
    return Math.round(num).toString();
  } else {
    // Format to at most 2 decimals, removing trailing zeros
    return Number.parseFloat(num.toFixed(2)).toString();
  }
}

type SortKey = "publication_date" | "publication_date_asc";

const cardListClassName =
  "flex flex-col divide-y divide-ds-slate-100 *:py-4 [&>*:first-child]:pt-0";

const sectionDividerClassName = "my-8 h-px w-full bg-ds-slate-100";

export default function JournalPage({ uid }: { uid: string }) {
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { activeLang, contentLang } = useActiveLang();
  // Journals only carry EN and FR content — collapse any other room language to EN
  // so publicationCount and article sections always count/query the same corpus.
  const journalLang: "fr" | "en" = contentLang === "fr" ? "fr" : "en";
  const { currentUser } = useCurrentUser();
  const isDesktop = isDesktopLayout();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { savePageScrollY, restorePageScrollY } = usePageScroll(scrollRef);

  const params = new URLSearchParams(location.search);
  const selectedMesh = params.get("mesh");
  const sort: SortKey =
    params.get("sort") === "publication_date_asc"
      ? "publication_date_asc"
      : "publication_date";
  const seeAllOnYourTopics = params.get("yourTopics") === "all";

  const journalQuery = useApiQuery("/webapp/private/journals/:uid", {
    param: { uid },
    query: { language: journalLang },
  });
  const journal = journalQuery.state === "success" ? journalQuery.body : null;

  const { isFollowed, toggleFollow } = useJournalFollow(
    journal === null ? null : { id: journal.id, name: journal.name }
  );

  // Round to the start of the day 7 days ago so the query key is stable across
  // renders (a fresh `new Date()` each render would thrash the cache).
  const publishedAfter = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const thisWeekQuery = useApiQuery(
    "/webapp/public/articles",
    journal === null
      ? false
      : {
          query: {
            limit: 6,
            offset: 0,
            sort: "publication_date",
            language: journalLang,
            journal: journal.id,
            publishedAfter,
          },
        }
  );
  const thisWeekArticles =
    thisWeekQuery.state === "success" ? thisWeekQuery.body.docs : [];

  const onYourTopicsQuery = useApiQuery(
    "/webapp/private/journals/:uid/on-your-topics",
    journal === null || currentUser === null
      ? false
      : {
          param: { uid },
          query: {
            language: journalLang,
            limit: seeAllOnYourTopics ? 24 : 6,
            offset: 0,
          },
        }
  );
  const onYourTopicsArticles =
    onYourTopicsQuery.state === "success" ? onYourTopicsQuery.body.docs : [];
  const onYourTopicsTotal =
    onYourTopicsQuery.state === "success"
      ? onYourTopicsQuery.body.meta.total
      : 0;
  const displayedOnYourTopics = seeAllOnYourTopics
    ? onYourTopicsArticles
    : onYourTopicsArticles.slice(0, 3);

  const meshTermsQuery = useApiQuery(
    "/webapp/private/journals/:uid/mesh-terms",
    journal === null ? false : { param: { uid } }
  );
  const meshTerms =
    meshTermsQuery.state === "success" ? meshTermsQuery.body.meshTerms : [];

  const articlesQuery = useApiInfiniteQuery(
    "/webapp/public/articles",
    journal === null
      ? false
      : {
          query: {
            limit: 20,
            sort,
            language: journalLang,
            journal: journal.id,
            meshTerm: selectedMesh === null ? undefined : selectedMesh,
          },
        }
  );
  const allArticles =
    articlesQuery.state === "success" ? articlesQuery.docs : [];

  useEffectOnce(() => {
    if (allArticles.length > 0) restorePageScrollY();
  }, []);

  const sortOptions = useMemo(
    () => [
      { value: "publication_date" as const, label: t("Most recent") },
      { value: "publication_date_asc" as const, label: t("Oldest first") },
    ],
    [t]
  );

  function setSearchParam(key: string, value: string | null) {
    const next = new URLSearchParams(location.search);
    if (value === null) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    history.replace({ pathname: location.pathname, search: next.toString() });
  }

  function handleScrollEnd() {
    if (articlesQuery.state === "success" && articlesQuery.canFetchNextPage) {
      articlesQuery.fetchNextPage();
    }
  }

  if (journalQuery.state === "error") return <Redirect to="/discovery" />;

  const isLoading = journalQuery.state === "pending" && journalQuery.isFetching;

  const metaParts =
    journal === null
      ? []
      : [
          `${journal.publicationCount.toLocaleString(activeLang)} ${t("publications")}`,
          ...(journal.impact_factor > 0
            ? [`${t("IF")} ${formatNumber(journal.impact_factor)}`]
            : []),
          ...(journal.sjr !== null && journal.sjr > 0
            ? [`${t("SJR")} ${formatNumber(journal.sjr)}`]
            : []),
        ];

  return (
    <ScrollableLayout
      scrollRef={scrollRef}
      headerClassName="bg-white/90 backdrop-blur-md"
      header={<ContentHeader transparent actions={<NavLang />} />}
      onScrollEnd={handleScrollEnd}
    >
      {isLoading || journal === null ? (
        <Loader expanded />
      ) : (
        <div className={isDesktop ? "mx-auto w-full max-w-190 pb-20" : "pb-10"}>
          {/* Header block */}
          <div className="flex flex-col gap-3 px-5 pt-3">
            <div className="flex flex-col gap-2">
              <h1 className="m-0 font-dm-sans text-2xl font-semibold tracking-tight text-ds-black">
                {journal.name}
              </h1>
              {metaParts.length > 0 && (
                <p className="m-0 text-footnote font-normal text-ds-slate-500">
                  {metaParts.join(" · ")}
                </p>
              )}
            </div>
            <Button
              variant="primary"
              size="md"
              className="w-full"
              onClick={() => {
                toggleFollow({ id: journal.id, name: journal.name });
              }}
            >
              {isFollowed ? t("Unfollow") : t("Follow")}
            </Button>
          </div>

          {/* This week */}
          {thisWeekArticles.length > 0 && (
            <>
              <div className={sectionDividerClassName} />
              <section className="flex flex-col gap-3 px-5">
                <SectionTitle title={t("This week")} />
                <div className={cardListClassName}>
                  {thisWeekArticles.map((article) => (
                    <div key={article.id}>
                      <ArticlePublicationCard
                        roomId={null}
                        article={article}
                        analyticsListName={AnalyticsItemList.JOURNAL_PAGE}
                        recommendation={null}
                        onClick={savePageScrollY}
                        className={null}
                        showImage={true}
                      />
                    </div>
                  ))}
                </div>
              </section>
            </>
          )}

          {/* On your topics */}
          {displayedOnYourTopics.length > 0 && (
            <>
              <div className={sectionDividerClassName} />
              <section className="flex flex-col gap-3 px-5">
                <SectionTitle title={t("On your topics")} />
                <div className={cardListClassName}>
                  {displayedOnYourTopics.map((article) => (
                    <div key={article.id}>
                      <ArticlePublicationCard
                        roomId={null}
                        article={article}
                        analyticsListName={AnalyticsItemList.JOURNAL_PAGE}
                        recommendation={null}
                        onClick={savePageScrollY}
                        className={null}
                        showImage={true}
                      />
                    </div>
                  ))}
                </div>
                {!seeAllOnYourTopics && onYourTopicsTotal > 3 && (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchParam("yourTopics", "all");
                    }}
                    className="flex items-center gap-1 self-start text-footnote font-medium text-ds-mint-800 hover:underline"
                  >
                    {t("See all on your topics")}
                    <IconChevronRight size={16} />
                  </button>
                )}
              </section>
            </>
          )}

          {/* All publications */}
          <div className={sectionDividerClassName} />
          <section className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5">
              <SectionTitle
                title={t("All publications")}
                className="shrink-0 whitespace-nowrap"
              />
              <SortSelect
                label={t("Sort by")}
                value={sort}
                options={sortOptions}
                onChange={(value) => {
                  setSearchParam("sort", value);
                }}
              />
            </div>

            {meshTerms.length > 0 && (
              <div className="flex w-full items-center gap-2 overflow-x-auto px-5 scrollbar-none [&::-webkit-scrollbar]:hidden">
                <TabButton
                  active={selectedMesh === null}
                  onClick={() => {
                    setSearchParam("mesh", null);
                  }}
                >
                  {t("All")}
                </TabButton>
                {meshTerms.map((meshTerm) => (
                  <TabButton
                    key={meshTerm.descriptorUi}
                    active={selectedMesh === meshTerm.descriptorUi}
                    icon={<IconHash size={16} stroke={1.5} />}
                    onClick={() => {
                      setSearchParam("mesh", meshTerm.descriptorUi);
                    }}
                  >
                    {meshTerm.label}
                  </TabButton>
                ))}
              </div>
            )}

            <div className="px-5">
              {articlesQuery.state === "pending" ? (
                <div className={cardListClassName}>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <SkeletonPublicationCard
                      showImage
                      key={index}
                      showDescription
                    />
                  ))}
                </div>
              ) : allArticles.length === 0 ? (
                <div className="flex items-center justify-center py-16">
                  <p className="text-base font-semibold text-ds-slate-500">
                    {t("There are no articles for this journal")}
                  </p>
                </div>
              ) : (
                <div className={cardListClassName}>
                  {allArticles.map((article) => (
                    <div key={article.id}>
                      <ArticlePublicationCard
                        roomId={null}
                        article={article}
                        analyticsListName={AnalyticsItemList.JOURNAL_PAGE}
                        recommendation={null}
                        onClick={savePageScrollY}
                        className={null}
                        showImage={true}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>
      )}
    </ScrollableLayout>
  );
}
